import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, MenuItem, Stack, TextField } from "@mui/material";
import { SelectEntidade } from "@/components/SelectEntidade";
import { Cancel, DeleteForever, Print, Sync, TableChart } from "@mui/icons-material";
import { FilterModal } from "@/components/FilterModal";
import usePagination from "@/hooks/pagination";
import { useNotification } from "@/context/NotificationContext";
import { DataTable } from "@/components/DataTable";
import { useFilterState } from "@/hooks/useFilterState";
import { useOpcoesCadastro } from "@/hooks/useOpcoesCadastro";
import { useAuth } from "@/context/AuthContext";
import { useGerenteDeEscala } from "@/hooks/useGerenteDeEscala";
import { fetchAllUsers } from "@/api/allUsers";
import type { User } from "@/api/user.service";
import { ConfirmModal } from "@/components/ConfirmModal";
import { StatusGeradaChip } from "@/components/escala/StatusGeradaChip";
import { EscalaEspelhoView } from "@/components/escala/EscalaEspelhoView";
import { LargeDialog } from "@/components/LargeDialog";
import {
  cancelarGerada,
  listEscalas,
  listGeradas,
  listLojas,
  lojaLabel,
  MESES,
  removerGerada,
  EscalaCaracteristicaLabels,
  EscalaTipoLabels,
  formatDataHoraBR,
  rotuloCompetencia,
  rotuloLoja,
  STATUS_GERADA_LABEL,
  type Escala,
  type Gerada,
  type Loja,
} from "@/api/escala.service";

// REMOVIDA nunca aparece nas listagens, então não é opção de filtro; CANCELADA é filtrada
// dinamicamente no componente (só Gerente ou acima a vê — mesma regra do backend, R52).
const STATUS_FILTRAVEIS_BASE = (Object.entries(STATUS_GERADA_LABEL) as [Gerada["status"], string][]).filter(
  ([status]) => status !== "REMOVIDA",
);

const ESCALA_EXCLUIDA = "Escala excluída";

// listar_escalas não traz mais a escala depois de excluída — a gerada antiga continua existindo,
// então cai aqui em vez de sumir da grade (R52).
function nomeDaEscala(escalas: Escala[], escalaId: string) {
  return escalas.find((e) => e.id === escalaId)?.nome ?? ESCALA_EXCLUIDA;
}

function lojaDaEscala(escalas: Escala[], lojas: Loja[], escalaId: string) {
  const escala = escalas.find((e) => e.id === escalaId);
  return escala ? rotuloLoja(lojas, escala.loja_id) : ESCALA_EXCLUIDA;
}

function nomeDoUsuario(usuarios: User[], id?: string) {
  return usuarios.find((u) => u.id === id)?.name ?? "";
}

export function EscalaGeradasTab() {
  const { user } = useAuth();
  const clientId = user!.loggedClient!.id;
  const { notify } = useNotification();
  const { podeVerRelatorios: gerente } = useGerenteDeEscala();
  const { total, setTotal, page, setPage, rowsPerPage, setRowsPerPage } = usePagination();

  // CANCELADA só aparece pra Gerente ou acima, nas lojas dele — mesma regra de geradas.go,
  // senão o filtro oferece um status que o backend sempre recusa devolver (R52).
  const STATUS_FILTRAVEIS = useMemo(
    () => STATUS_FILTRAVEIS_BASE.filter(([status]) => status !== "CANCELADA" || gerente),
    [gerente],
  );

  const [geradas, setGeradas] = useState<Gerada[]>([]);
  // Só vira true na primeira carga e nunca mais volta — recargas seguintes não trocam
  // toolbar/tabela/espelho aberto por um spinner de página inteira (R47, mesmo padrão de
  // useListaRemota). Sem isso, recarregar com a lista vazia (filtro sem resultado, ou o
  // onEstadoAlterado do espelho depois de validar/aprovar) desmontava o LargeDialog do espelho
  // aberto por cima.
  const [carregouAlgumaVez, setCarregouAlgumaVez] = useState(false);
  const chamada = useRef(0);
  // espelhoDe fica setado ao fechar, pra o conteúdo não sumir durante a
  // animação de saída do dialog.
  const [espelhoDe, setEspelhoDe] = useState<Gerada | null>(null);
  const [espelhoAberto, setEspelhoAberto] = useState(false);
  // Gerada aguardando impressão pelo atalho da linha — monta um EscalaEspelhoView escondido (fora
  // da tela) só pra carregar os dados e disparar o print, sem abrir o dialog do espelho.
  const [imprimindoDe, setImprimindoDe] = useState<Gerada | null>(null);
  const finalizarImpressao = useCallback(() => setImprimindoDe(null), []);
  // Gerada aguardando confirmação de remoção (null = nenhuma).
  const [removendoDe, setRemovendoDe] = useState<Gerada | null>(null);
  const [removendo, setRemovendo] = useState(false);
  // Gerada aguardando confirmação de cancelamento (null = nenhuma).
  const [cancelandoDe, setCancelandoDe] = useState<Gerada | null>(null);
  const [cancelando, setCancelando] = useState(false);

  const abrirEspelho = (gerada: Gerada) => {
    setEspelhoDe(gerada);
    setEspelhoAberto(true);
  };

  const confirmarRemocao = () => {
    if (!removendoDe) return;
    setRemovendo(true);
    removerGerada(removendoDe.id, clientId)
      .then(() => {
        notify("Escala gerada removida", "success");
        setRemovendoDe(null);
        loadData();
      })
      .catch((e) => notify(e?.message || "Erro ao remover a escala gerada", "error"))
      .finally(() => setRemovendo(false));
  };

  const confirmarCancelamento = () => {
    if (!cancelandoDe) return;
    setCancelando(true);
    cancelarGerada(cancelandoDe.id, clientId)
      .then(() => {
        notify("Escala cancelada", "success");
        setCancelandoDe(null);
        loadData();
      })
      .catch((e) => notify(e?.message || "Erro ao cancelar a escala gerada", "error"))
      .finally(() => setCancelando(false));
  };

  const { inputValues, setInputValues, filtros, applyFilters, handleClearFilters } = useFilterState(
    { nome: "", loja_id: "", escala_id: "", ano: "", mes: "", status: "" as Gerada["status"] | "" },
    () => setPage(0),
  );

  // escala.listar_geradas devolve só escala_id, sem nome nem loja — carregado
  // à parte pra resolver a coluna e alimentar os dois filtros por Autocomplete.
  // Cada lista resolvida de forma independente — uma falhar (ex.: papel sem
  // acesso a listar_lojas) não pode zerar a outra também.
  const { opcoes: { escalas, lojas, usuarios } } = useOpcoesCadastro<{
    escalas: Escala[];
    lojas: Loja[];
    usuarios: User[];
  }>(
    {
      escalas: {
        buscar: async () => (await listEscalas({ page: 1, page_size: 1000 }, clientId)).items,
        mensagemErro: "Erro ao carregar escalas",
      },
      lojas: { buscar: () => listLojas(clientId), mensagemErro: "Erro ao carregar lojas" },
      // Só pra resolver "gerado por" na coluna — o legado mostrava, este ainda não (R52).
      usuarios: { buscar: fetchAllUsers, mensagemErro: "Erro ao carregar usuários" },
    },
    [clientId],
  );

  const loadData = async () => {
    const minha = ++chamada.current;
    try {
      const result = await listGeradas(
        {
          escala_id: filtros.escala_id || undefined,
          loja_id: filtros.loja_id || undefined,
          nome: filtros.nome || undefined,
          ano: filtros.ano ? Number(filtros.ano) : undefined,
          mes: filtros.mes ? Number(filtros.mes) : undefined,
          status: filtros.status || undefined,
          page: page + 1,
          page_size: rowsPerPage,
        },
        clientId,
      );
      if (minha !== chamada.current) return;
      setGeradas(result.items ?? []);
      setTotal(result.total ?? 0);
    } catch (error) {
      if (minha === chamada.current) notify("Erro ao carregar escalas geradas", "error");
    } finally {
      if (minha === chamada.current) setCarregouAlgumaVez(true);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, filtros, page, rowsPerPage]);

  const activeFilterCount = Object.values(filtros).filter((v) => v).length;

  if (!carregouAlgumaVez) return <Sync />;

  return (
    <>
      <Stack direction="row" sx={{ mb: 2 }}>
        <FilterModal activeCount={activeFilterCount} onApply={applyFilters} onClear={handleClearFilters}>
          <TextField
            label="Nome"
            size="small"
            fullWidth
            value={inputValues.nome}
            onChange={(e) => setInputValues((v) => ({ ...v, nome: e.target.value }))}
          />
          <SelectEntidade
            label="Loja"
            options={lojas}
            value={inputValues.loja_id}
            onChange={(id) => setInputValues((v) => ({ ...v, loja_id: id ?? "" }))}
            getLabel={lojaLabel}
            size="small"
          />
          <SelectEntidade
            label="Escala"
            options={escalas}
            value={inputValues.escala_id}
            onChange={(id) => setInputValues((v) => ({ ...v, escala_id: id ?? "" }))}
            getLabel={(e) => e.nome}
            size="small"
          />
          <TextField
            select
            label="Mês"
            size="small"
            fullWidth
            value={inputValues.mes}
            onChange={(e) => setInputValues((v) => ({ ...v, mes: e.target.value }))}
          >
            <MenuItem value="">Todos</MenuItem>
            {MESES.map((nome, i) => (
              <MenuItem key={nome} value={String(i + 1)}>
                {nome}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Ano"
            type="number"
            size="small"
            fullWidth
            value={inputValues.ano}
            onChange={(e) => setInputValues((v) => ({ ...v, ano: e.target.value }))}
          />
          <TextField
            select
            label="Status"
            size="small"
            fullWidth
            value={inputValues.status}
            onChange={(e) => setInputValues((v) => ({ ...v, status: e.target.value as Gerada["status"] | "" }))}
          >
            <MenuItem value="">Todos</MenuItem>
            {STATUS_FILTRAVEIS.map(([valor, rotulo]) => (
              <MenuItem key={valor} value={valor}>
                {rotulo}
              </MenuItem>
            ))}
          </TextField>
        </FilterModal>
      </Stack>

      <DataTable
        data={geradas}
        columns={[
          { key: "escala_id", label: "Escala", render: (g) => nomeDaEscala(escalas, g.escala_id) },
          { key: "loja", label: "Loja", render: (g) => lojaDaEscala(escalas, lojas, g.escala_id) },
          {
            key: "tipo",
            label: "Tipo",
            render: (g) => {
              const escala = escalas.find((e) => e.id === g.escala_id);
              return escala ? EscalaTipoLabels[escala.tipo] : "";
            },
          },
          {
            key: "caracteristica",
            label: "Característica",
            render: (g) => {
              const escala = escalas.find((e) => e.id === g.escala_id);
              return escala?.caracteristica ? EscalaCaracteristicaLabels[escala.caracteristica] : "";
            },
          },
          { key: "mes", label: "Competência", render: (g) => rotuloCompetencia(g.ano, g.mes) },
          { key: "versao", label: "Versão" },
          { key: "total_colaboradores", label: "Colaboradores", render: (g) => String(g.total_colaboradores ?? 0) },
          {
            key: "status",
            label: "Status",
            render: (g) => <StatusGeradaChip status={g.status} />,
          },
          {
            key: "gerada_em",
            label: "Gerada em",
            render: (g) => formatDataHoraBR(g.gerada_em),
          },
          { key: "gerada_por", label: "Gerado por", render: (g) => nomeDoUsuario(usuarios, g.gerada_por) },
        ]}
        actions={(g) => (
          <>
            {/* Cancelar só em RASCUNHO (01-dominio.md) — o backend ainda confere autoria/prazo. */}
            {g.status === "RASCUNHO" && (
              <IconButton
                aria-label={`Cancelar geração de ${nomeDaEscala(escalas, g.escala_id)}`}
                color="error"
                onClick={() => setCancelandoDe(g)}
              >
                <Cancel />
              </IconButton>
            )}
            {/* Remover é definitivo (01-dominio.md: "some para todos") — só Gerente ou acima,
                e só sobre uma já cancelada. */}
            {gerente && g.status === "CANCELADA" && (
              <IconButton
                aria-label={`Remover geração de ${nomeDaEscala(escalas, g.escala_id)}`}
                color="error"
                onClick={() => setRemovendoDe(g)}
              >
                <DeleteForever />
              </IconButton>
            )}
            {/* Atalho de impressão: só aparece aprovada, mesma regra do botão dentro do espelho.
                Fica antes de "Ver espelho", que fica sempre o mais à direita possível. */}
            {g.status === "APROVADA" && (
              <IconButton
                aria-label={`Imprimir ${nomeDaEscala(escalas, g.escala_id)}`}
                disabled={imprimindoDe?.id === g.id}
                onClick={() => setImprimindoDe(g)}
              >
                <Print />
              </IconButton>
            )}
            <IconButton aria-label={`Ver espelho de ${nomeDaEscala(escalas, g.escala_id)}`} onClick={() => abrirEspelho(g)}>
              <TableChart />
            </IconButton>
          </>
        )}
        onRowClick={abrirEspelho}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
      />

      <LargeDialog open={espelhoAberto} title="Espelho da escala" onClose={() => setEspelhoAberto(false)}>
        {espelhoDe && <EscalaEspelhoView geradaId={espelhoDe.id} onEstadoAlterado={loadData} />}
      </LargeDialog>

      {/* Atalho de impressão: carrega o espelho fora da tela e dispara o print sozinho, sem
          abrir o dialog acima — o usuário só vê o diálogo de impressão do navegador. `inert` tira
          os botões reais do espelho (Desaprovar etc.) da navegação por teclado e da árvore de
          acessibilidade enquanto ficam escondidos — `aria-hidden` sozinho não bloqueia o foco. */}
      {imprimindoDe && (
        <Box sx={{ position: "fixed", top: 0, left: "-9999px", width: 0, height: 0, overflow: "hidden" }} inert>
          {/* key força remontar ao trocar de alvo: o autoImprimiuRef interno só pode disparar
              uma vez por instância, senão trocar de gerada no meio de uma impressão anterior
              trava o botão de imprimir dela em disabled pra sempre (imprimindoDe nunca zera). */}
          <EscalaEspelhoView key={imprimindoDe.id} geradaId={imprimindoDe.id} autoImprimir onImpressaoConcluida={finalizarImpressao} />
        </Box>
      )}

      <ConfirmModal
        open={cancelandoDe !== null}
        title="Cancelar escala gerada"
        text="A grade desta geração é apagada e a escala sai das listagens. Esta ação não desfaz. Cancelar mesmo assim?"
        btnText="Cancelar escala"
        btnColor="error"
        loading={cancelando}
        onConfirm={confirmarCancelamento}
        onClose={() => setCancelandoDe(null)}
      />

      <ConfirmModal
        open={removendoDe !== null}
        title="Remover escala gerada"
        text="Remove de vez esta geração cancelada — some para todos, sem volta. Remover mesmo assim?"
        btnText="Remover"
        btnColor="error"
        loading={removendo}
        onConfirm={confirmarRemocao}
        onClose={() => setRemovendoDe(null)}
      />
    </>
  );
}
